import { z } from "zod";

export const LAYOUT_NODE_TYPES = [
  "MACHINE",
  "EQUIPMENT",
  "SENSOR",
  "SCALE",
  "CONTROL_POINT",
  "INSPECTION",
  "HUMAN_STEP",
  "GROUP",
  "START",
  "END",
] as const;

export const LAYOUT_EDGE_TYPES = [
  "PRODUCTION_FLOW",
  "MEASUREMENT_FLOW",
  "SENSOR_LINK",
  "DEPENDENCY",
  "QUALITY_CHECK",
  "ALTERNATIVE_FLOW",
  "PARALLEL_FLOW",
] as const;

export const CanvasNodeSchema = z.object({
  id: z.string().uuid(),
  type: z.enum(LAYOUT_NODE_TYPES),
  label: z.string().min(1).max(120),
  x: z.number().finite(),
  y: z.number().finite(),
  data: z.record(z.unknown()).default({}),
});

export const CanvasEdgeSchema = z.object({
  id: z.string().uuid(),
  sourceNodeId: z.string().uuid(),
  targetNodeId: z.string().uuid(),
  type: z.enum(LAYOUT_EDGE_TYPES).default("PRODUCTION_FLOW"),
  data: z.record(z.unknown()).default({}),
});

export const CanvasViewportSchema = z.object({
  x: z.number().finite(),
  y: z.number().finite(),
  zoom: z.number().min(0.1).max(4),
});

export const UpdateCanvasRequestSchema = z
  .object({
    nodes: z.array(CanvasNodeSchema),
    edges: z.array(CanvasEdgeSchema),
    viewport: CanvasViewportSchema.optional(),
  })
  .superRefine((data, ctx) => {
    const startCount = data.nodes.filter((n) => n.type === "START").length;
    const endCount = data.nodes.filter((n) => n.type === "END").length;
    if (startCount !== 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["nodes"],
        message: `Canvas deve conter exatamente 1 nó de início (encontrado: ${startCount}).`,
      });
    }
    if (endCount !== 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["nodes"],
        message: `Canvas deve conter exatamente 1 nó de fim (encontrado: ${endCount}).`,
      });
    }

    const nodeIds = new Set(data.nodes.map((n) => n.id));
    for (const edge of data.edges) {
      if (!nodeIds.has(edge.sourceNodeId)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["edges"],
          message: `Conexão ${edge.id} referencia nó de origem inexistente: ${edge.sourceNodeId}.`,
        });
      }
      if (!nodeIds.has(edge.targetNodeId)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["edges"],
          message: `Conexão ${edge.id} referencia nó de destino inexistente: ${edge.targetNodeId}.`,
        });
      }
    }
  });

export const CanvasResponseSchema = z.object({
  nodes: z.array(CanvasNodeSchema),
  edges: z.array(CanvasEdgeSchema),
  viewport: CanvasViewportSchema.nullable().optional(),
});

export type CanvasNode = z.infer<typeof CanvasNodeSchema>;
export type CanvasEdge = z.infer<typeof CanvasEdgeSchema>;
export type CanvasViewport = z.infer<typeof CanvasViewportSchema>;
export type UpdateCanvasRequest = z.infer<typeof UpdateCanvasRequestSchema>;
export type CanvasResponse = z.infer<typeof CanvasResponseSchema>;

export const CreateCanvasNodeRequestSchema = CanvasNodeSchema.omit({ id: true });
export type CreateCanvasNodeRequest = z.infer<typeof CreateCanvasNodeRequestSchema>;

export const CreateCanvasEdgeRequestSchema = CanvasEdgeSchema.omit({ id: true });
export type CreateCanvasEdgeRequest = z.infer<typeof CreateCanvasEdgeRequestSchema>;
